import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaLayerGroup }     from '@react-icons/all-files/fa/FaLayerGroup';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }            from '@react-icons/all-files/fa/FaCog';
import { FaHome }           from '@react-icons/all-files/fa/FaHome';
import { FaPlus }           from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb    from '../../../../commons/Breadcrumb';
import PageHeader    from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { EditButton, DeleteButton, RenderActions } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../manager/workflow-clase.reducer';

const BASE = '/configuracion/workflow/clase';

export default function ClasePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef   = useRef<HTMLDivElement>(null);

  const entities      = useAppSelector(s => s.workflowClase.entities);
  const totalItems    = useAppSelector(s => s.workflowClase.totalItems);
  const loading       = useAppSelector(s => s.workflowClase.loading);
  const updating      = useAppSelector(s => s.workflowClase.updating);
  const updateSuccess = useAppSelector(s => s.workflowClase.updateSuccess);

  const [rows, setRows]             = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast }                        = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  // Toast from form page (create / update)
  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) {
      showToast('success', msg);
      window.history.replaceState({}, '');
    }
  }, []);

  // After delete success
  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Clase eliminada correctamente.');
    }
  }, [updateSuccess]);

  // Keep confirm button in loading state while deleting
  useEffect(() => {
    setConfirmLoading(updating);
  }, [updating]);

  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar <strong>{params.row.nombre}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar Clase', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id',        headerName: 'ID',     flex: 0.5 },
    { field: 'nombre',    headerName: 'Nombre', flex: 4 },
    { field: '__actions', headerName: '',        flex: 1, renderCell: renderActions },
  ];

  const filters = [
    { label: 'Nombre', name: 'nombre' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio',        to: '/',                       icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion',          icon: <FaCog /> },
        { label: 'Workflow',      to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Clase',                                        icon: <FaLayerGroup /> },
      ]} />

      <PageHeader
        icon={<FaLayerGroup />}
        iconColor="#6366f1"
        title="Clase"
        subtitle="Gestión de clases de workflow"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nueva Clase</>,
          onClick: () => navigate(`${BASE}/new`),
        }}
      />

      {/* Confirmation banner */}
      <Confirm config={confirmConfig} />

      {/* Toast notifications */}
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
